/**
 * Product Inventory Per Market
 *
 * Fetches live inventory levels from a Cloudflare Worker and displays a
 * context-aware message based on three factors:
 *   1. The user's current market (US, AU, GB, etc.)
 *   2. Stock levels at US and AU warehouse locations
 *   3. Whether the product has the relevant preorder tag in Shopify
 *
 * ─────────────────────────────────────────────────────────────────
 * PREORDER TAGS
 * ─────────────────────────────────────────────────────────────────
 *
 *   allow_preorder     — enables preorder for AU and all international markets
 *   allow_preorder_us  — enables preorder for the US market only
 *
 *   These tags are independent. A product can have one, both, or neither.
 *
 * ─────────────────────────────────────────────────────────────────
 * MESSAGE LOGIC
 * ─────────────────────────────────────────────────────────────────
 *
 * US market
 *   Stock is read from US warehouse locations only.
 *   Preorder is triggered when US stock = 0 AND the product has the
 *   "allow_preorder_us" tag.
 *   Without the tag, zero US stock shows "Sold Out".
 *
 *   Examples:
 *     US stock: 5,  allow_preorder_us: any  → "In Stock"
 *     US stock: 2,  allow_preorder_us: any  → "Low stock - 2 items available"
 *     US stock: 1,  allow_preorder_us: any  → "Low stock - 1 item available"
 *     US stock: 0,  allow_preorder_us: yes  → Preorder  (ATC button label changes to preorder CTA)
 *     US stock: 0,  allow_preorder_us: no   → "Sold Out"
 *
 * AU market
 *   Stock is read from all AU warehouse locations (summed together).
 *   Preorder is triggered when AU stock = 0 AND the product has the
 *   "allow_preorder" tag.
 *   Without the tag, zero AU stock shows "Sold Out".
 *
 *   Examples:
 *     AU stock: 5,  allow_preorder: any  → "In Stock"
 *     AU stock: 2,  allow_preorder: any  → "Low stock - 2 items available"
 *     AU stock: 0,  allow_preorder: yes  → Preorder
 *     AU stock: 0,  allow_preorder: no   → "Sold Out"
 *
 * GB market (and all other non-US markets)
 *   Follows the same logic as AU — stock is read from AU warehouse locations
 *   and the "allow_preorder" tag controls whether a zero-stock product shows
 *   as preorder or sold out.
 *
 *   Examples:
 *     AU stock: 5,  allow_preorder: any  → "In Stock"
 *     AU stock: 0,  allow_preorder: yes  → Preorder
 *     AU stock: 0,  allow_preorder: no   → "Sold Out"
 *
 * ─────────────────────────────────────────────────────────────────
 * PREORDER BEHAVIOUR
 * ─────────────────────────────────────────────────────────────────
 * When preorder is active the script also:
 *   - Replaces the Add-to-Cart button label with the preorder CTA text
 *   - Opens a preorder info modal when the ATC button is clicked
 *
 * Message priority (highest → lowest): Preorder > Sold Out > Low Stock > In Stock
 */

const classes = {
  hiddenLabel: "hidden-label",
  inventoryLow: "inventory--low",
  preorderModalActive: "modal--is-active",
};

const selectors = {
  inventoryPerMarketBlock: "[data-inventory-per-market]",
  inventoryPerMarketCurrentMarket: "data-current-market",
  inventoryPerMarketCurrentVariantID: "data-current-variant-id",
  inventoryPerMarketInStockMessage: "data-in-stock-message",
  inventoryPerMarketDefaultPreorderMessage: "data-default-preorder-message",
  inventoryPerMarketVariantPreorderMessage: "data-variant-preorder-message",
  inventoryPerMarketPreorderCTA: "data-preorder-cta",
  inventoryPerMarketHasPreorderTag: "data-product-has-preorder-tag",
  inventoryPerMarketHasPreorderTagUS: "data-product-has-preorder-tag-us",
  inventoryPerMarketLoadingMessage: "[data-loading-message]",
  inventoryPerMarketMessageContainer: "[data-message-container]",
  inventoryPerMarketPreorderModal: "[data-preorder-modal]",
};

const getVariantIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("variant");
};

// ─── F1-F4 hardening (Stef bug 3 May 08:13) ────────────────────────────────
// F1. AbortController per call so a new variant cancels the in-flight fetch.
// F2. Debounce rapid pushState/replaceState bursts (~80ms).
// F3. Per-variant success cache (~30s TTL) so the same variant doesn't
//     oscillate between "In Stock" / "Sold Out" if the worker returns
//     inconsistent payloads.
// F4. On fetch error, keep the last successful state for the variant if
//     cached; otherwise leave the loading state in place rather than
//     replacing the block with a user-facing "Inventory data unavailable"
//     string. We log a console error so support still has visibility.
const INVENTORY_CACHE_TTL_MS = 30_000;
const INVENTORY_DEBOUNCE_MS = 80;
const inventoryCache = new Map(); // variantId -> { ts, data }
let inFlightController = null;
let debounceTimer = null;

const updateInventoryPerMarket = (
  variantIdOverride = null,
  root = document,
) => {
  const inventoryPerMarketBlock = root.querySelector(
    selectors.inventoryPerMarketBlock,
  );

  if (!inventoryPerMarketBlock) return;

  const currentMarket = inventoryPerMarketBlock.getAttribute(
    selectors.inventoryPerMarketCurrentMarket,
  );

  const currentVariantID =
    variantIdOverride ||
    inventoryPerMarketBlock.getAttribute(
      selectors.inventoryPerMarketCurrentVariantID,
    );

  if (!currentVariantID) return;

  const outOfStockMessage = "Sold Out";
  const inStockMessage = inventoryPerMarketBlock.getAttribute(
    selectors.inventoryPerMarketInStockMessage,
  );
  const defaultPreorderMessage = inventoryPerMarketBlock.getAttribute(
    selectors.inventoryPerMarketDefaultPreorderMessage,
  );
  const variantPreorderMessage = inventoryPerMarketBlock.getAttribute(
    selectors.inventoryPerMarketVariantPreorderMessage,
  );
  const preorderCTA = inventoryPerMarketBlock.getAttribute(
    selectors.inventoryPerMarketPreorderCTA,
  );

  const loadingMessage = inventoryPerMarketBlock.querySelector(
    selectors.inventoryPerMarketLoadingMessage,
  );

  const messageContainer = inventoryPerMarketBlock.querySelector(
    selectors.inventoryPerMarketMessageContainer,
  );

  // Reset UI for variant switch
  loadingMessage?.classList.remove(classes.hiddenLabel);
  messageContainer?.classList.add(classes.hiddenLabel);

  // ─── Renderer factored out so cache hits + fresh responses share a path ──
  const renderInventory = (data) => {
    const levels = data.levels || [];

      const hasPreorderTag =
        inventoryPerMarketBlock.getAttribute(
          selectors.inventoryPerMarketHasPreorderTag,
        ) === "true";

      const hasPreorderTagUS =
        inventoryPerMarketBlock.getAttribute(
          selectors.inventoryPerMarketHasPreorderTagUS,
        ) === "true";

      // Combine all AU locations (e.g. Coghlan + Special Orders)
      const auStock = levels
        .filter((level) => level.country_code === "AU")
        .reduce((sum, level) => sum + level.available, 0);

      const usStock = levels
        .filter((level) => level.country_code === "US")
        .reduce((sum, level) => sum + level.available, 0);

      let shouldPreorder = false;

      if (currentMarket === "US") {
        // US: preorder only when US stock is 0 and the product has the allow_preorder_us tag
        shouldPreorder = usStock <= 0 && hasPreorderTagUS;
      } else {
        // AU and all other markets: preorder only when AU is out of stock
        // and the product has the allow_preorder tag
        shouldPreorder = auStock <= 0 && hasPreorderTag;
      }

      // Stock level for the current market (US looks at US, everyone else looks at AU)
      const marketStock = currentMarket === "US" ? usStock : auStock;
      const isOutOfStock = !shouldPreorder && marketStock <= 0;
      const isLowStock = !shouldPreorder && !isOutOfStock && marketStock < 4;

      // Pick the right message based on priority: preorder > sold out > low stock > in stock
      let blockMessage;
      if (shouldPreorder) {
        blockMessage = variantPreorderMessage || defaultPreorderMessage;
      } else if (isOutOfStock) {
        blockMessage = outOfStockMessage;
      } else if (isLowStock) {
        const itemWord = marketStock === 1 ? "item" : "items";
        blockMessage = `Low stock - ${marketStock} ${itemWord} available`;
      } else {
        blockMessage = inStockMessage;
      }

      if (shouldPreorder || isOutOfStock || isLowStock) {
        messageContainer.classList.add(classes.inventoryLow);
      }

      // Resolve the ATC button once for use across all three state branches.
      // `.payment-buttons` only wraps the button when dynamic checkout buttons
      // are enabled — fall back to the plain form selector so the override
      // also fires when dynamic buttons are off (#38).
      const productBlocksContainer = messageContainer.closest(
        "[data-product-blocks]",
      );
      const addToCartButton = productBlocksContainer
        ? productBlocksContainer.querySelector(
            ".product-single__form .payment-buttons [data-add-to-cart]",
          ) ||
          productBlocksContainer.querySelector(
            ".product-single__form [data-add-to-cart]",
          )
        : null;
      const addToCartText =
        addToCartButton?.querySelector("[data-add-to-cart-text]") || null;

      if (shouldPreorder) {
        // Update Add-to-Cart Button Label & Trigger Modal
        if (productBlocksContainer && preorderCTA != "" && addToCartButton) {
          const preorderModal =
            root.querySelector(selectors.inventoryPerMarketPreorderModal) ||
            document.querySelector(selectors.inventoryPerMarketPreorderModal);

          if (addToCartText) {
            addToCartText.textContent = preorderCTA;
          }

          // #38 — Olga Berg preorder fix (Luna A-Line Clutch).
          // product-form.liquid renders the ATC button with
          // `disabled="disabled"` whenever `current_variant.available` is
          // false. The legacy `isPreorder` Liquid flag (literal "Pre Order"
          // tag) exempts the label but NOT the disabled attribute, and the
          // new per-market `allow_preorder` / `allow_preorder_us` tags can
          // only be evaluated client-side after the inventory fetch.
          // Re-enable the button so a zero-stock preorder variant can be
          // added to cart and checked out, matching the expected behaviour.
          addToCartButton.removeAttribute("disabled");
          addToCartButton.disabled = false;
          if (addToCartButton.getAttribute("aria-disabled") === "true") {
            addToCartButton.removeAttribute("aria-disabled");
          }
          if (preorderModal) {
            // #38 (D — OB4832_BLK) — renderInventory re-runs on every
            // variant switch (history.pushState/replaceState hook), so the
            // click listeners below were re-attached each render. On a
            // multi-variant product clicking the ATC button then fired the
            // modal-open handler N times, and stale handlers from a previous
            // preorder variant persisted after switching to a non-preorder
            // variant. Guard each listener with a one-time dataset flag so it
            // attaches exactly once per element regardless of re-render count.
            if (addToCartButton.dataset.preorderModalBound !== "true") {
              addToCartButton.dataset.preorderModalBound = "true";
              // Trigger Modal
              addToCartButton.addEventListener("click", () => {
                preorderModal.classList.add(classes.preorderModalActive);
                document.documentElement.classList.add("modal-open");
              });
            }

            if (preorderModal.dataset.preorderModalBound !== "true") {
              preorderModal.dataset.preorderModalBound = "true";

              // Close Button
              const closeModalButton =
                preorderModal.querySelector(".modal__close");
              closeModalButton?.addEventListener("click", () => {
                preorderModal.classList.remove(classes.preorderModalActive);
                document.documentElement.classList.remove("modal-open");
              });

              // Click outside modal to close
              preorderModal.addEventListener("click", (event) => {
                // Check if the click is inside .modal__inner
                if (!event.target.closest(".modal__inner")) {
                  preorderModal.classList.remove(
                    classes.preorderModalActive,
                  );
                  document.documentElement.classList.remove("modal-open");
                }
              });
            }
          }
        }
      } else if (isOutOfStock) {
        // #38 Zoey 19-May — tag-overrides-CSW sold-out gate.
        // When the per-market preorder tag is absent and stock is zero, force
        // the ATC button to "Sold Out" + disabled even if Shopify's "continue
        // selling when out of stock" is on. Without the tag, the product
        // shouldn't be backorderable on the affected market.
        if (addToCartButton) {
          if (addToCartText) {
            addToCartText.textContent = outOfStockMessage;
          }
          addToCartButton.setAttribute("disabled", "disabled");
          addToCartButton.disabled = true;
          addToCartButton.setAttribute("aria-disabled", "true");
        }
      } else if (addToCartButton) {
        // In-stock / low-stock — reset the button. Covers the variant-switch
        // case where we previously forced "Sold Out"/"Pre Order" labels and
        // need to return to default state when the new variant is sellable.
        if (addToCartText) {
          addToCartText.textContent = "Add to cart";
        }
        addToCartButton.removeAttribute("disabled");
        addToCartButton.disabled = false;
        if (addToCartButton.getAttribute("aria-disabled") === "true") {
          addToCartButton.removeAttribute("aria-disabled");
        }
      }

      // #38 — Port from v7 (commit 4271596). Klaviyo BIS "EMAIL ME WHEN
      // AVAILABLE" mounts whenever Shopify reports variant.available=false.
      // Per Stef + Alex spec (#22 / PR #4): Pre-Order and Sold Out are
      // mutually exclusive; Notify Me belongs only under the Sold Out path.
      // Show BIS strictly on sold-out, hide on preorder + in-stock.
      const bisTrigger = document.getElementById("BIS_trigger");
      if (bisTrigger) {
        bisTrigger.style.display = isOutOfStock ? "" : "none";
      }

      messageContainer.querySelector("span:nth-child(2)").textContent =
        `${blockMessage}`;

      loadingMessage?.classList.add(classes.hiddenLabel);
      messageContainer?.classList.remove(classes.hiddenLabel);
  };

  // ─── F3. Cache check ────────────────────────────────────────────────────
  const cached = inventoryCache.get(currentVariantID);
  if (cached && Date.now() - cached.ts < INVENTORY_CACHE_TTL_MS) {
    renderInventory(cached.data);
    return;
  }

  // ─── F1. Cancel any in-flight fetch before starting a new one ───────────
  if (inFlightController) {
    inFlightController.abort();
  }
  inFlightController = new AbortController();
  const signal = inFlightController.signal;

  fetch(
    `https://shopify-admin-api-variant-inventory.stef-050.workers.dev?variant_id=${currentVariantID}`,
    { signal },
  )
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch inventory data");
      return res.json();
    })
    .then((data) => {
      inventoryCache.set(currentVariantID, { ts: Date.now(), data });
      renderInventory(data);
    })
    .catch((err) => {
      // F1: AbortError is expected when a newer variant takes over — silent.
      if (err.name === "AbortError") return;
      console.error("Error fetching inventory:", err);
      // F4. Fall back to last successful state if available; otherwise leave
      // the loading message in place rather than replacing the entire block
      // with a user-facing "Inventory data unavailable" string.
      const fallback = inventoryCache.get(currentVariantID);
      if (fallback) {
        renderInventory(fallback.data);
      }
    });
};

// F2. Debounced wrapper for URL-change-driven refetches. Direct callers
// (DOMContentLoaded, quickview:open) keep calling updateInventoryPerMarket
// directly so initial render is immediate.
const debouncedUpdateFromUrl = (variantId) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    updateInventoryPerMarket(variantId);
  }, INVENTORY_DEBOUNCE_MS);
};

(function () {
  const pushState = history.pushState;
  const replaceState = history.replaceState;

  const onUrlChange = () => {
    const urlVariantId = getVariantIdFromUrl();
    debouncedUpdateFromUrl(urlVariantId);
  };

  history.pushState = function () {
    pushState.apply(history, arguments);
    onUrlChange();
  };

  history.replaceState = function () {
    replaceState.apply(history, arguments);
    onUrlChange();
  };
})();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    updateInventoryPerMarket();
  });
} else {
  updateInventoryPerMarket();
}

document.addEventListener("quickview:open", () => {
  const initQuickViewInventory = () => {
    const quickShopModal = document.querySelector(
      ".modal--quick-shop.modal--is-active",
    );

    if (!quickShopModal) return false;

    const inventoryBlock = quickShopModal.querySelector(
      selectors.inventoryPerMarketBlock,
    );

    if (!inventoryBlock) return false;

    const variantId = inventoryBlock.getAttribute(
      selectors.inventoryPerMarketCurrentVariantID,
    );

    // Give the function a variant ID to look-up,
    // ... and make sure its looking inside the modal and not document (for making changes)
    updateInventoryPerMarket(variantId, quickShopModal);

    return true;
  };

  if (initQuickViewInventory()) return;

  const observer = new MutationObserver((_, obs) => {
    if (initQuickViewInventory()) {
      obs.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
